/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\MOHSourceConfig.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Music on Hold Sources module.
----------------------------------------------------------------*/


import React from 'react';
import { Plus, Play, Pause, Volume2 } from 'lucide-react';
import {
  DndContext, closestCenter, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/tabs';
import { SortableFileItem } from '../components';
import { buildApiUrl, getApiHeaders } from '../../../config/api';

// Minimal type slices needed from parent hooks
interface FileItem { name: string; path?: string; size?: number; }

interface FileUploadHook {
  existingFiles: FileItem[];
  selectedFiles: File[];
  existingChimeFiles: FileItem[];
  selectedChimeFiles: File[];
  filePreviews: Map<string, string>;
  chimeFilePreviews: Map<string, string>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  chimeInputRef: React.RefObject<HTMLInputElement>;
  setExistingFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  setExistingChimeFiles: React.Dispatch<React.SetStateAction<FileItem[]>>;
  removeFile: (name: string) => void;
  removeExistingFile: (name: string) => void;
  removeChimeFile: (name: string) => void;
  removeExistingChimeFile: (name: string) => void;
}

interface AudioPreviewHook {
  isPlaying: boolean;
  isChimePlaying: boolean;
  isStreamPlaying: boolean;
  playingFileIndex: number | string | null;
  playingChimeIndex: number | string | null;
  audioRef: React.RefObject<HTMLAudioElement>;
  chimeAudioRef: React.RefObject<HTMLAudioElement>;
  audioUrl: string | null;
  chimeAudioUrl: string | null;
  playFile: (url: string, index: number | string) => void;
  playChimeServerFile: (id: string, name: string, key: string) => Promise<void>;
  playServerFile: (id: string, name: string, key: string) => Promise<void>;
}

interface FormData {
  source_type: string;
  source_path: string;
  play_mode: string;
  interval: number;
  chime_enabled?: boolean;
  chime_freq?: number;
}

interface MOHSourceConfigProps {
  formData: FormData;
  handleInputChange: (field: string, value: unknown) => void;
  fileUpload: FileUploadHook;
  audioPreview: AudioPreviewHook;
  handleDragEnd: (event: DragEndEvent) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleChimeFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTestStream: () => void;
  handleRemoveFile: (name: string) => void;
  sensors: ReturnType<typeof import('@dnd-kit/core').useSensors>;
  id: string | undefined;
  activeFileTab: 'music' | 'chime';
  setActiveFileTab: (tab: 'music' | 'chime') => void;
  showError: (msg: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export function MOHSourceConfig({
  formData, handleInputChange, fileUpload, audioPreview,
  handleDragEnd, handleFileInputChange, handleChimeFileInputChange,
  handleTestStream, handleRemoveFile, sensors, id,
  activeFileTab, setActiveFileTab, showError, t,
}: MOHSourceConfigProps) {
  return (
    <FormBox
      title={t('mohSources.sections.source') || 'Source Configuration'}
      description={t('mohSources.sections.sourceDescription') || 'Configure the audio source'}
    >
      {formData.source_type === 'file' && (
        <>
          {/* Mode and Interval */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center mb-6">
            <SimpleTooltip content={t('mohSources.tooltips.playMode') || t('mohSources.fields.playMode')} position="top" maxWidth="300px">
              <Label htmlFor="play_mode" className="text-sm font-medium text-foreground">
                {t('mohSources.fields.playMode') || 'Mode'} <span className="text-red-500">*</span>
              </Label>
            </SimpleTooltip>
            <Select value={formData.play_mode} onValueChange={(v) => handleInputChange('play_mode', v)}
              options={[
                { value: 'linear', label: t('mohSources.playModes.linear') || 'Linear (In Order)' },
                { value: 'shuffle', label: t('mohSources.playModes.shuffle') || 'Shuffle (Random Order)' },
                { value: 'random', label: t('mohSources.playModes.random') || 'Random (Each Play)' },
              ]} />
            <SimpleTooltip content={t('mohSources.tooltips.interval') || t('mohSources.fields.interval')} position="top" maxWidth="300px">
              <Label htmlFor="interval" className="text-sm font-medium text-foreground">
                {t('mohSources.fields.interval') || 'Interval (seconds)'}
              </Label>
            </SimpleTooltip>
            <Input id="interval" type="number" min="1" max="120" value={formData.interval}
              onChange={(e) => handleInputChange('interval', parseInt(e.target.value) || 20)} />
          </div>

          {/* Music + Chime Tabs */}
          <div className="mt-6 border-t border-border pt-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground mb-4">{t('mohSources.fields.uploadFile') || 'Upload Files'}</h3>
              <Tabs value={activeFileTab} onValueChange={(v) => setActiveFileTab(v as 'music' | 'chime')}>
                <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
                  <TabsTrigger value="music">{t('mohSources.tabs.musicPlaylist') || 'Music Playlist'}</TabsTrigger>
                  <TabsTrigger value="chime">{t('mohSources.tabs.chimeAnnouncements') || 'Chime & Announcements'}</TabsTrigger>
                </TabsList>

                {/* Music Tab */}
                <TabsContent value="music">
                  <div className="overflow-x-auto w-full">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <table className="w-full border-collapse table-fixed">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-center py-3 px-2 text-sm font-medium text-foreground" style={{ width: '5%' }}>{t('mohSources.tableHeaders.move') || 'Move'}</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '35%' }}>{t('mohSources.tableHeaders.fileName') || 'File Name'}</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '15%' }}>{t('mohSources.tableHeaders.size') || 'Size'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '15%' }}>{t('mohSources.tableHeaders.status') || 'Status'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '10%' }}>{t('mohSources.tableHeaders.play') || 'Play'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '10%' }}>{t('mohSources.tableHeaders.actions') || 'Actions'}</th>
                          </tr>
                        </thead>
                        <SortableContext
                          items={[...fileUpload.existingFiles.map((_, i) => `existing-${i}`), ...fileUpload.selectedFiles.map((_, i) => `file-${i}`)]}
                          strategy={verticalListSortingStrategy}
                          disabled={formData.play_mode !== 'linear'}>
                          <tbody>
                            {fileUpload.existingFiles.length === 0 && fileUpload.selectedFiles.length === 0 ? (
                              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">{t('mohSources.messages.noFiles') || 'No files added yet.'}</td></tr>
                            ) : (
                              <>
                                {fileUpload.existingFiles.map((file, index) => {
                                  const fileKey = `existing-${index}`;
                                  const isPlaying = audioPreview.isPlaying && audioPreview.playingFileIndex === fileKey;
                                  const handlePlay = async () => {
                                    if (!id) return;
                                    try {
                                      if (!file.size) {
                                        const headUrl = buildApiUrl(`/api/telephony/moh-sources/${id}/file/${encodeURIComponent(file.name)}`);
                                        const r = await fetch(headUrl, { method: 'HEAD', headers: getApiHeaders() });
                                        if (r.ok) {
                                          const len = r.headers.get('Content-Length');
                                          if (len) { const s = parseInt(len, 10); if (!isNaN(s) && s > 0) fileUpload.setExistingFiles(prev => prev.map(f => f.name === file.name ? { ...f, size: s } : f)); }
                                        }
                                      }
                                      await audioPreview.playServerFile(id, file.name, fileKey);
                                    } catch (err) { showError((err as Error).message || 'Failed to load audio'); }
                                  };
                                  return <SortableFileItem key={fileKey} id={fileKey} file={file} index={index} previewUrl={null} isCurrentPlaying={isPlaying} isPlaying={isPlaying} playMode={formData.play_mode as 'linear' | 'random' | 'shuffle'} onPlay={handlePlay} onRemove={() => fileUpload.removeExistingFile(file.name)} fileSize={file.size} t={t} isExisting={true} />;
                                })}
                                {fileUpload.selectedFiles.map((file, index) => {
                                  const previewUrl = fileUpload.filePreviews.get(file.name);
                                  const globalIndex = fileUpload.existingFiles.length + index;
                                  const fileId = `file-${index}`;
                                  const isCurrent = globalIndex === audioPreview.playingFileIndex;
                                  return <SortableFileItem key={fileId} id={fileId} file={file} index={globalIndex} previewUrl={previewUrl || null} isCurrentPlaying={isCurrent} isPlaying={audioPreview.isPlaying && isCurrent} playMode={formData.play_mode as 'linear' | 'random' | 'shuffle'} onPlay={() => { if (previewUrl) audioPreview.playFile(previewUrl, globalIndex); }} onRemove={() => handleRemoveFile(file.name)} fileSize={file.size} t={t} isExisting={false} />;
                                })}
                              </>
                            )}
                          </tbody>
                        </SortableContext>
                      </table>
                    </DndContext>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={() => fileUpload.fileInputRef.current?.click()} size="sm">
                      <Plus className="h-4 w-4 mr-2" />{t('mohSources.fields.addFiles') || 'Add Files'}
                    </Button>
                  </div>
                  <input ref={fileUpload.fileInputRef} type="file" accept=".wav,.mp3,.ogg,audio/wav,audio/mpeg,audio/ogg" multiple onChange={handleFileInputChange} className="hidden" />
                </TabsContent>

                {/* Chime Tab */}
                <TabsContent value="chime">
                  <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    <SimpleTooltip content={t('mohSources.tooltips.enabled') || 'Enable or disable chime'} position="top" maxWidth="300px">
                      <Label htmlFor="chime_enabled" className="text-sm font-medium text-foreground">{t('mohSources.fields.enabled') || 'Enabled'}</Label>
                    </SimpleTooltip>
                    <div className="flex justify-start">
                      <Toggle id="chime_enabled" value={formData.chime_enabled || false} onChange={(v) => handleInputChange('chime_enabled', v)} />
                    </div>
                    <SimpleTooltip content={t('mohSources.tooltips.chimeFreq') || t('mohSources.fields.chimeFreq')} position="top" maxWidth="300px">
                      <Label htmlFor="chime_freq" className="text-sm font-medium text-foreground">{t('mohSources.fields.chimeFreq') || 'Frequency (seconds)'}</Label>
                    </SimpleTooltip>
                    <Input id="chime_freq" type="number" min="10" max="600" value={formData.chime_freq || 90}
                      onChange={(e) => handleInputChange('chime_freq', parseInt(e.target.value) || 90)} />
                  </div>

                  <div className="overflow-x-auto w-full">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <table className="w-full border-collapse table-fixed">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-center py-3 px-2 text-sm font-medium text-foreground" style={{ width: '5%' }}>{t('mohSources.tableHeaders.move') || 'Move'}</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '35%' }}>{t('mohSources.tableHeaders.fileName') || 'File Name'}</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '15%' }}>{t('mohSources.tableHeaders.size') || 'Size'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '15%' }}>{t('mohSources.tableHeaders.status') || 'Status'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '10%' }}>{t('mohSources.tableHeaders.play') || 'Play'}</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-foreground" style={{ width: '10%' }}>{t('mohSources.tableHeaders.actions') || 'Actions'}</th>
                          </tr>
                        </thead>
                        <SortableContext
                          items={[...fileUpload.existingChimeFiles.map((_, i) => `chime-existing-${i}`), ...fileUpload.selectedChimeFiles.map((_, i) => `chime-file-${i}`)]}
                          strategy={verticalListSortingStrategy}
                          disabled={false}>
                          <tbody>
                            {fileUpload.existingChimeFiles.length === 0 && fileUpload.selectedChimeFiles.length === 0 ? (
                              <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">{t('mohSources.messages.noFiles') || 'No chime files added yet.'}</td></tr>
                            ) : (
                              <>
                                {fileUpload.existingChimeFiles.map((file, index) => {
                                  const key = `chime-existing-${index}`;
                                  const isPlaying = audioPreview.isChimePlaying && audioPreview.playingChimeIndex === key;
                                  const handlePlay = async () => {
                                    if (!id) return;
                                    try {
                                      if (!file.size) {
                                        const headUrl = buildApiUrl(`/api/telephony/moh-sources/${id}/file/${encodeURIComponent(file.name)}`);
                                        const r = await fetch(headUrl, { method: 'HEAD', headers: getApiHeaders() });
                                        if (r.ok) {
                                          const len = r.headers.get('Content-Length');
                                          if (len) { const s = parseInt(len, 10); if (!isNaN(s) && s > 0) fileUpload.setExistingChimeFiles(prev => prev.map(f => f.name === file.name ? { ...f, size: s } : f)); }
                                        }
                                      }
                                      await audioPreview.playChimeServerFile(id, file.name, key);
                                    } catch (err) { showError((err as Error).message || 'Failed to load chime audio'); }
                                  };
                                  return <SortableFileItem key={key} id={key} file={file} index={index} previewUrl={null} isCurrentPlaying={isPlaying} isPlaying={isPlaying} playMode="linear" onPlay={handlePlay} onRemove={() => fileUpload.removeExistingChimeFile(file.name)} fileSize={file.size} t={t} isExisting={true} />;
                                })}
                                {fileUpload.selectedChimeFiles.map((file, index) => {
                                  const previewUrl = fileUpload.chimeFilePreviews.get(file.name);
                                  const fileId = `chime-file-${index}`;
                                  const isCurrent = fileId === audioPreview.playingChimeIndex;
                                  return <SortableFileItem key={fileId} id={fileId} file={file} index={fileUpload.existingChimeFiles.length + index} previewUrl={previewUrl || null} isCurrentPlaying={isCurrent} isPlaying={audioPreview.isChimePlaying && isCurrent} playMode="linear"
                                    onPlay={() => { if (previewUrl) audioPreview.playFile(previewUrl, fileId); }}
                                    onRemove={() => fileUpload.removeChimeFile(file.name)} fileSize={file.size} t={t} isExisting={false} />;
                                })}
                              </>
                            )}
                          </tbody>
                        </SortableContext>
                      </table>
                    </DndContext>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button type="button" onClick={() => fileUpload.chimeInputRef.current?.click()} size="sm">
                      <Plus className="h-4 w-4 mr-2" />{t('mohSources.fields.addFiles') || 'Add Files'}
                    </Button>
                  </div>
                  <input ref={fileUpload.chimeInputRef} type="file" accept=".wav,.mp3,.ogg,audio/wav,audio/mpeg,audio/ogg" multiple onChange={handleChimeFileInputChange} className="hidden" />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </>
      )}

      {formData.source_type === 'stream' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            <SimpleTooltip content={t('mohSources.tooltips.streamUrl') || t('mohSources.fields.streamUrl')} position="top" maxWidth="300px">
              <Label htmlFor="source_path" className="text-sm font-medium text-foreground">
                {t('mohSources.fields.streamUrl') || 'Stream URL'} <span className="text-red-500">*</span>
              </Label>
            </SimpleTooltip>
            <Input id="source_path" className="col-span-2" value={formData.source_path}
              onChange={(e) => handleInputChange('source_path', e.target.value)}
              placeholder={t('mohSources.fields.streamUrlPlaceholder') || 'http://example.com/stream.mp3'} required />
            <div className="flex justify-start">
              <Button type="button" variant={audioPreview.isStreamPlaying ? 'destructive' : 'default'} onClick={handleTestStream}>
                {audioPreview.isStreamPlaying ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                {t('mohSources.fields.testStream') || 'Test Stream'}
              </Button>
            </div>
          </div>
          {audioPreview.isStreamPlaying && (
            <div className="mt-2 text-sm text-muted-foreground flex items-center">
              <Volume2 className="h-4 w-4 mr-2" />
              {t('mohSources.fields.testStreamHint') || 'Stream is playing. Click the button again to stop.'}
            </div>
          )}
        </>
      )}

      {/* Hidden audio elements - managed by useAudioPreview hook */}
      <audio ref={audioPreview.audioRef} src={audioPreview.audioUrl || undefined} className="hidden" preload="metadata" crossOrigin="anonymous" />
      <audio ref={audioPreview.chimeAudioRef} src={audioPreview.chimeAudioUrl || undefined} className="hidden" preload="metadata" crossOrigin="anonymous" />
    </FormBox>
  );
}
